import { Box, Popover, Portal } from "@chakra-ui/react"
import useHoverCardOrPopover from "@pathwright/ux/src/components/chakra/useHoverCardOrPopover"
import type { ComponentProps, ReactNode } from "react"

export type PopoverTipInteraction = "hover" | "click" | "always-open"

export type PopoverTipProps = {
  children: ReactNode
  interaction: PopoverTipInteraction
  renderBody: () => ReactNode
  showIndicator?: boolean
  renderIndicator?: () => ReactNode
  // True=always show; False=never show; Undefined=default based on interaction
  displayArrow?: boolean
  positioning?: ComponentProps<typeof Popover.Root>["positioning"]
  hoverProps?: {
    openDelay?: number
    closeDelay?: number
    lazyMount?: boolean
  }
}

const defaultContentProps = {
  bg: "white",
  color: "gray.800",
  border: "none",
  borderRadius: "xl",
  boxShadow: "xl",
  maxW: "320px",
  p: 0
} as const

type TipPortalCompound =
  | typeof Popover
  | ReturnType<typeof useHoverCardOrPopover>

function PopoverTipPortalLayer({
  compound: C,
  showArrow,
  renderBody
}: {
  compound: TipPortalCompound
  showArrow: boolean
  renderBody: () => ReactNode
}) {
  return (
    <Portal>
      <C.Positioner>
        <C.Content {...defaultContentProps}>
          {showArrow ? (
            <C.Arrow>
              <C.ArrowTip borderColor="transparent" />
            </C.Arrow>
          ) : null}
          {renderBody()}
        </C.Content>
      </C.Positioner>
    </Portal>
  )
}

export function PopoverTip({
  children,
  interaction,
  renderBody,
  showIndicator = false,
  renderIndicator,
  displayArrow,
  positioning = { placement: "bottom" },
  hoverProps = {
    openDelay: 200,
    closeDelay: 250,
    lazyMount: true
  }
}: PopoverTipProps) {
  const HoverCardOrPopover = useHoverCardOrPopover()

  const showArrow =
    displayArrow !== undefined ? displayArrow : interaction !== "always-open"

  const wrappedAnchor = (
    <Box display="inline-block" pos="relative">
      {children}
      {showIndicator ? renderIndicator?.() : null}
    </Box>
  )

  if (interaction === "click" || interaction === "always-open") {
    const pinned = interaction === "always-open"
    return (
      <Popover.Root
        {...(pinned
          ? {
              open: true,
              closeOnInteractOutside: false
            }
          : {
              lazyMount: hoverProps.lazyMount,
              closeOnInteractOutside: true,
              closeOnEscape: true
            })}
        positioning={positioning}
        autoFocus={false}
      >
        {pinned ? (
          <Popover.Anchor>{wrappedAnchor}</Popover.Anchor>
        ) : (
          <Popover.Trigger asChild>{wrappedAnchor}</Popover.Trigger>
        )}
        <PopoverTipPortalLayer
          compound={Popover}
          showArrow={showArrow}
          renderBody={renderBody}
        />
      </Popover.Root>
    )
  }

  return (
    <HoverCardOrPopover.Root
      lazyMount={hoverProps.lazyMount}
      openDelay={hoverProps.openDelay}
      closeDelay={hoverProps.closeDelay}
      positioning={positioning}
    >
      <HoverCardOrPopover.Trigger asChild>
        {wrappedAnchor}
      </HoverCardOrPopover.Trigger>
      <PopoverTipPortalLayer
        compound={HoverCardOrPopover}
        showArrow={showArrow}
        renderBody={renderBody}
      />
    </HoverCardOrPopover.Root>
  )
}
